/**
 * Accessibility Manager
 * Coordinates all accessibility features for the game
 * Implements WCAG 2.1 AA compliance
 */

import { ColorBlindFilters, ColorBlindMode } from './ColorBlindFilters';
import { FontSizeManager, FontSizeLevel } from './FontSizeManager';
import { KeyboardNavigation } from './KeyboardNavigation';
import { ScreenReaderAnnouncer } from './ScreenReaderAnnouncer';
import { MotionManager } from './MotionManager';

const STORAGE_KEY = 'cellsandlife_accessibility';

export interface AccessibilitySettings {
  colorBlindMode: ColorBlindMode;
  highContrast: boolean;
  fontSize: FontSizeLevel;
  reduceMotion: boolean;
  keyboardOnly: boolean;
  dwellClickEnabled: boolean;
  dwellClickTime: number;
  screenReaderMode: boolean;
}

const DEFAULT_SETTINGS: AccessibilitySettings = {
  colorBlindMode: 'none',
  highContrast: false,
  fontSize: 'medium',
  reduceMotion: false,
  keyboardOnly: false,
  dwellClickEnabled: false,
  dwellClickTime: 1000,
  screenReaderMode: false,
};

export class AccessibilityManager {
  private static instance: AccessibilityManager | null = null;

  private settings: AccessibilitySettings;
  private colorBlindFilters: ColorBlindFilters;
  private fontSizeManager: FontSizeManager;
  private keyboardNavigation: KeyboardNavigation;
  private screenReaderAnnouncer: ScreenReaderAnnouncer;
  private motionManager: MotionManager;

  private listeners: Set<(settings: AccessibilitySettings) => void> = new Set();

  private constructor() {
    this.settings = this.loadSettings();

    // Initialize subsystems
    this.colorBlindFilters = new ColorBlindFilters();
    this.fontSizeManager = new FontSizeManager();
    this.keyboardNavigation = new KeyboardNavigation();
    this.screenReaderAnnouncer = new ScreenReaderAnnouncer();
    this.motionManager = new MotionManager();

    // Apply initial settings
    this.applyAllSettings();

    // Listen for system preferences
    this.detectSystemPreferences();
  }

  static getInstance(): AccessibilityManager {
    if (!AccessibilityManager.instance) {
      AccessibilityManager.instance = new AccessibilityManager();
    }
    return AccessibilityManager.instance;
  }

  private loadSettings(): AccessibilitySettings {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('Failed to load accessibility settings:', e);
    }
    return { ...DEFAULT_SETTINGS };
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (e) {
      console.warn('Failed to save accessibility settings:', e);
    }
  }

  private detectSystemPreferences(): void {
    // Detect reduced motion preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (motionQuery.matches && !this.settings.reduceMotion) {
        this.settings.reduceMotion = true;
        this.motionManager.setReduceMotion(true);
      }

      motionQuery.addEventListener('change', (e) => {
        if (e.matches) {
          this.setReduceMotion(true);
        }
      });

      // Detect high contrast preference
      const contrastQuery = window.matchMedia('(prefers-contrast: more)');
      if (contrastQuery.matches && !this.settings.highContrast) {
        this.settings.highContrast = true;
        this.colorBlindFilters.setHighContrast(true);
      }

      contrastQuery.addEventListener('change', (e) => {
        if (e.matches) {
          this.setHighContrast(true);
        }
      });
    }
  }

  private applyAllSettings(): void {
    this.colorBlindFilters.setMode(this.settings.colorBlindMode);
    this.colorBlindFilters.setHighContrast(this.settings.highContrast);
    this.fontSizeManager.setSize(this.settings.fontSize);
    this.motionManager.setReduceMotion(this.settings.reduceMotion);
    this.keyboardNavigation.setEnabled(this.settings.keyboardOnly);
    this.screenReaderAnnouncer.setEnabled(this.settings.screenReaderMode);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.settings));
  }

  // Public API

  getSettings(): AccessibilitySettings {
    return { ...this.settings };
  }

  onChange(listener: (settings: AccessibilitySettings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Color Blind Settings
  setColorBlindMode(mode: ColorBlindMode): void {
    this.settings.colorBlindMode = mode;
    this.colorBlindFilters.setMode(mode);
    this.saveSettings();
    this.notifyListeners();
  }

  setHighContrast(enabled: boolean): void {
    this.settings.highContrast = enabled;
    this.colorBlindFilters.setHighContrast(enabled);
    this.saveSettings();
    this.notifyListeners();
  }

  // Font Size Settings
  setFontSize(size: FontSizeLevel): void {
    this.settings.fontSize = size;
    this.fontSizeManager.setSize(size);
    this.saveSettings();
    this.notifyListeners();
  }

  getFontSizes(): { body: number; heading: number; label: number; small: number } {
    return this.fontSizeManager.getSizes();
  }

  // Motion Settings
  setReduceMotion(enabled: boolean): void {
    this.settings.reduceMotion = enabled;
    this.motionManager.setReduceMotion(enabled);
    this.saveSettings();
    this.notifyListeners();
  }

  shouldReduceMotion(): boolean {
    return this.motionManager.shouldReduceMotion();
  }

  // Keyboard Navigation
  setKeyboardOnly(enabled: boolean): void {
    this.settings.keyboardOnly = enabled;
    this.keyboardNavigation.setEnabled(enabled);
    this.saveSettings();
    this.notifyListeners();
  }

  // Dwell Click
  setDwellClick(enabled: boolean, time?: number): void {
    this.settings.dwellClickEnabled = enabled;
    if (time !== undefined) {
      this.settings.dwellClickTime = time;
    }
    this.saveSettings();
    this.notifyListeners();
  }

  getDwellClickSettings(): { enabled: boolean; time: number } {
    return {
      enabled: this.settings.dwellClickEnabled,
      time: this.settings.dwellClickTime,
    };
  }

  // Screen Reader
  setScreenReaderMode(enabled: boolean): void {
    this.settings.screenReaderMode = enabled;
    this.screenReaderAnnouncer.setEnabled(enabled);
    this.saveSettings();
    this.notifyListeners();
  }

  announce(text: string, priority: 'polite' | 'assertive' = 'polite'): void {
    this.screenReaderAnnouncer.announce(text, priority);
  }

  // Game-specific announcements
  announceZoomLevel(level: number): void {
    this.screenReaderAnnouncer.announceZoomLevel(level);
  }

  announceScanProgress(percent: number): void {
    this.screenReaderAnnouncer.announceScanProgress(percent);
  }

  announceScanResult(isLiving: boolean, cellType: string): void {
    this.screenReaderAnnouncer.announceScanResult(isLiving, cellType);
  }

  announceClassificationResult(correct: boolean, feedback?: string): void {
    this.screenReaderAnnouncer.announceClassificationResult(correct, feedback);
  }

  announceEvidenceCollected(description: string): void {
    this.screenReaderAnnouncer.announceEvidenceCollected(description);
  }

  // Subsystem access
  getMotionManager(): MotionManager {
    return this.motionManager;
  }

  getKeyboardNavigation(): KeyboardNavigation {
    return this.keyboardNavigation;
  }

  getColorBlindFilters(): ColorBlindFilters {
    return this.colorBlindFilters;
  }

  getFontSizeManager(): FontSizeManager {
    return this.fontSizeManager;
  }

  // Reset to defaults
  resetToDefaults(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.applyAllSettings();
    this.saveSettings();
    this.notifyListeners();
  }
}

// Export singleton getter
export function getAccessibilityManager(): AccessibilityManager {
  return AccessibilityManager.getInstance();
}
